/**
 * Stage 5 — storey clustering (pure).
 *
 * Groups room patches into storeys by floor height alone: rooms of one storey
 * share a floor, while their ceilings may legitimately differ (dropped
 * ceilings, stairwell voids) — ceiling deviation therefore never splits a
 * storey. A patch matching no band becomes its own proposed level; small
 * levels nested inside another storey's z-range are flagged as sub-levels
 * (mezzanines/split-levels).
 */

import * as THREE from 'three';
import type { DetectedFloorLevel, DetectedFloorPlane, RoomPatch } from './types';
import type { FloorDetectionTuning } from './tuning';

interface StoreyAccumulator {
  patches: RoomPatch[];
  /** Area-weighted running mean of member floorZ. */
  floorZ: number;
  area: number;
}

/** Area-weighted median of a per-patch value: the member value at which half
 *  the total area is reached — always an actual member height. */
function areaWeightedMedian(patches: RoomPatch[], value: (p: RoomPatch) => number): number {
  const sorted = [...patches].sort((a, b) => value(a) - value(b));
  const half = sorted.reduce((sum, p) => sum + p.areaM2, 0) / 2;
  let acc = 0;
  for (const p of sorted) {
    acc += p.areaM2;
    if (acc >= half) return value(p);
  }
  return value(sorted[sorted.length - 1]);
}

function weightedMeans(patches: RoomPatch[]): { floorZ: number; ceilingZ: number; area: number } {
  let area = 0;
  let floorSum = 0;
  let ceilingSum = 0;
  for (const p of patches) {
    area += p.areaM2;
    floorSum += p.floorZ * p.areaM2;
    ceilingSum += p.ceilingZ * p.areaM2;
  }
  return { floorZ: floorSum / area, ceilingZ: ceilingSum / area, area };
}

/**
 * Union of the patches' bounds, padded horizontally by the wall allowance:
 * patches stop at the inner wall faces, so without the padding the walls fall
 * outside footprint-based clips. Z is left exact.
 */
function unionBounds(patches: RoomPatch[], wallPadding: number): THREE.Box3 {
  const box = new THREE.Box3();
  for (const p of patches) box.union(p.bounds);
  box.min.x -= wallPadding;
  box.min.y -= wallPadding;
  box.max.x += wallPadding;
  box.max.y += wallPadding;
  return box;
}

/**
 * Frame for oriented footprints: the user's drawn selection rectangle — so
 * proposals come out aligned with the box the user drew instead of with
 * world X/Y — plus the column grid geometry needed to place patch cells.
 */
export interface FootprintFrame {
  /** Selection rectangle corners in draw order; c1 − c0 is the frame's x axis. */
  areaCorners: THREE.Vector2[];
  grid: { nx: number; cellSize: number; originX: number; originY: number };
}

/**
 * Tight rectangle around the patches' cells expressed in the selection's
 * frame, padded like `unionBounds`. Corners come out in the `[bl, br, tr, tl]`
 * order `FloorToolBase.boxCorners` uses. Cell CENTRES are projected rather
 * than the AABB's corners — projecting an axis-aligned box into a rotated
 * frame inflates it by up to ~41%. Undefined for a degenerate frame or
 * patches without cell data.
 */
function orientedFootprintCorners(patches: RoomPatch[], frame: FootprintFrame, wallPadding: number): THREE.Vector2[] | undefined {
  const [c0, c1] = frame.areaCorners;
  if (!c0 || !c1) return undefined;
  const u = new THREE.Vector2().subVectors(c1, c0);
  if (u.lengthSq() < 1e-12) return undefined;
  u.normalize();
  const v = new THREE.Vector2(-u.y, u.x);
  const { nx, cellSize, originX, originY } = frame.grid;

  let minS = Infinity;
  let maxS = -Infinity;
  let minT = Infinity;
  let maxT = -Infinity;
  for (const patch of patches) {
    for (const col of patch.cells) {
      const cx = originX + ((col % nx) + 0.5) * cellSize;
      const cy = originY + (Math.floor(col / nx) + 0.5) * cellSize;
      const s = cx * u.x + cy * u.y;
      const t = cx * v.x + cy * v.y;
      if (s < minS) minS = s;
      if (s > maxS) maxS = s;
      if (t < minT) minT = t;
      if (t > maxT) maxT = t;
    }
  }
  if (!Number.isFinite(minS)) return undefined;

  // From a cell's centre, its corners reach up to half the cell diagonal in
  // any frame direction (exactly half a cell only when the frame is
  // axis-aligned) — cover the cell in every rotation, then add the wall pad.
  const pad = wallPadding + (cellSize * Math.SQRT2) / 2;
  minS -= pad;
  maxS += pad;
  minT -= pad;
  maxT += pad;
  const at = (s: number, t: number): THREE.Vector2 => new THREE.Vector2(s * u.x + t * v.x, s * u.y + t * v.y);
  return [at(minS, minT), at(maxS, minT), at(maxS, maxT), at(minS, maxT)];
}

export interface ClusteredStoreys {
  levels: DetectedFloorLevel[];
  /** Storey index per input patch (parallel to `patches`); -1 = the patch's aggregate was dropped as a slab-crossing artifact. */
  patchStoreyIndex: number[];
}

/**
 * Cluster patches into proposed levels, sorted by floor height ascending.
 * With a `frame`, each level also gets `footprintCorners` — its footprint as
 * a rectangle aligned with the user's selection (the AABB `footprint` stays,
 * the overlap tests below and box-based consumers rely on it).
 */
export function clusterStoreys(patches: RoomPatch[], tuning: FloorDetectionTuning, frame?: FootprintFrame): ClusteredStoreys {
  if (patches.length === 0) return { levels: [], patchStoreyIndex: [] };

  // Greedy banding, biggest patches first so large rooms seed the bands.
  const order = patches.map((_, i) => i).sort((a, b) => patches[b].areaM2 - patches[a].areaM2);
  const storeys: StoreyAccumulator[] = [];
  const storeyOfPatch = new Array<number>(patches.length).fill(-1);

  for (const patchIndex of order) {
    const patch = patches[patchIndex];
    let target = -1;
    for (let s = 0; s < storeys.length; s++) {
      if (Math.abs(patch.floorZ - storeys[s].floorZ) <= tuning.STOREY_FLOOR_TOLERANCE) {
        target = s;
        break;
      }
    }
    if (target === -1) {
      storeys.push({ patches: [patch], floorZ: patch.floorZ, area: patch.areaM2 });
      storeyOfPatch[patchIndex] = storeys.length - 1;
    } else {
      const storey = storeys[target];
      storey.floorZ = (storey.floorZ * storey.area + patch.floorZ * patch.areaM2) / (storey.area + patch.areaM2);
      storey.area += patch.areaM2;
      storey.patches.push(patch);
      storeyOfPatch[patchIndex] = target;
    }
  }

  // Second pass: greedy order can split borderline bands — merge storeys whose
  // re-averaged floor heights ended up within tolerance.
  for (let a = 0; a < storeys.length; a++) {
    for (let b = a + 1; b < storeys.length; b++) {
      if (Math.abs(storeys[a].floorZ - storeys[b].floorZ) <= tuning.STOREY_FLOOR_TOLERANCE) {
        storeys[a].patches.push(...storeys[b].patches);
        const merged = weightedMeans(storeys[a].patches);
        storeys[a].floorZ = merged.floorZ;
        storeys[a].area = merged.area;
        for (let i = 0; i < storeyOfPatch.length; i++) {
          if (storeyOfPatch[i] === b) storeyOfPatch[i] = a;
          else if (storeyOfPatch[i] > b) storeyOfPatch[i] -= 1;
        }
        storeys.splice(b, 1);
        b -= 1;
      }
    }
  }

  // Aggregate each storey.
  interface StoreyAggregate {
    floorZ: number;
    ceilingZ: number;
    area: number;
    footprint: THREE.Box3;
    /** Selection-aligned footprint rectangle; only with a frame. */
    footprintCorners?: THREE.Vector2[];
    patches: RoomPatch[];
    floorPointCount: number;
    ceilingPointCount: number;
    floorZSpread: number;
    isSubLevel: boolean;
    slabGapWarning: boolean;
    originalIndex: number;
  }

  const aggregates: StoreyAggregate[] = storeys.map((storey, originalIndex) => {
    let area = 0;
    let floorPointCount = 0;
    let ceilingPointCount = 0;
    let minFloor = Infinity;
    let maxFloor = -Infinity;
    for (const p of storey.patches) {
      area += p.areaM2;
      floorPointCount += p.floorPointCount;
      ceilingPointCount += p.ceilingPointCount;
      minFloor = Math.min(minFloor, p.floorZ);
      maxFloor = Math.max(maxFloor, p.floorZ);
    }
    return {
      // Area-weighted MEDIAN, not a mean and not the extremes: the height at
      // which half the storey's area is reached. Always an ACTUAL member
      // room's height (a mean blends heights into one no room has), and a
      // single small outlier room can't drag it (the extremes let exactly
      // that happen). The running weighted mean still drives the BANDING.
      floorZ: areaWeightedMedian(storey.patches, (p) => p.floorZ),
      ceilingZ: areaWeightedMedian(storey.patches, (p) => p.ceilingZ),
      area,
      footprint: unionBounds(storey.patches, tuning.FOOTPRINT_WALL_PADDING_M),
      footprintCorners: frame ? orientedFootprintCorners(storey.patches, frame, tuning.FOOTPRINT_WALL_PADDING_M) : undefined,
      patches: storey.patches,
      floorPointCount,
      ceilingPointCount,
      floorZSpread: maxFloor - minFloor,
      isSubLevel: false,
      slabGapWarning: false,
      originalIndex,
    };
  });

  // Slab-crossing artifacts: a smaller level that STARTS inside a larger
  // storey's interior and ENDS above that storey's ceiling would span the
  // slab — no real room does that. These are staircase/landing leftovers
  // (the patch floor-banding keeps rooms, but a landing band can clear the
  // area filter). Mezzanines survive (their ceiling stays BELOW the host's)
  // and split-levels survive (their footprint lies BESIDE the host's).
  const overlapFractionOfFirst = (a: THREE.Box3, b: THREE.Box3): number => {
    const w = Math.min(a.max.x, b.max.x) - Math.max(a.min.x, b.min.x);
    const h = Math.min(a.max.y, b.max.y) - Math.max(a.min.y, b.min.y);
    if (w <= 0 || h <= 0) return 0;
    const areaA = (a.max.x - a.min.x) * (a.max.y - a.min.y);
    return areaA > 0 ? (w * h) / areaA : 0;
  };
  // Margins anchor to physical meaning, not the banding tolerance: the floor
  // must sit well INSIDE the host's interior air (above the furniture zone —
  // a floor a few cm up is a split-level step, not mid-air), and the ceiling
  // must poke past the host's ceiling by more than lamp/beam clearance.
  const crossesSlabOfLargerStorey = (s: StoreyAggregate): boolean =>
    aggregates.some(
      (t) =>
        t !== s &&
        t.area > s.area &&
        s.floorZ > t.floorZ + tuning.FURNITURE_IGNORE_HEIGHT &&
        s.floorZ < t.ceilingZ &&
        s.ceilingZ > t.ceilingZ + tuning.CEILING_CLEARANCE &&
        overlapFractionOfFirst(s.footprint, t.footprint) >= 0.5,
    );
  const kept = aggregates.filter((s) => !crossesSlabOfLargerStorey(s));

  // Sub-level detection: small area + z-range nested inside another storey.
  // A mezzanine has usable space BENEATH it, so its floor must sit at least
  // the furniture-zone height above the host's floor — a floor a few cm up
  // is a split-level neighbor, not a mezzanine — and it must lie mostly
  // inside the host's footprint.
  const maxArea = Math.max(...kept.map((s) => s.area));
  for (const storey of kept) {
    if (storey.area >= tuning.SUB_LEVEL_AREA_FRACTION * maxArea) continue;
    const nested = kept.some(
      (other) =>
        other !== storey &&
        storey.floorZ > other.floorZ + tuning.FURNITURE_IGNORE_HEIGHT &&
        storey.ceilingZ <= other.ceilingZ + tuning.CEILING_CLEARANCE &&
        overlapFractionOfFirst(storey.footprint, other.footprint) >= 0.5,
    );
    if (nested) storey.isSubLevel = true;
  }

  kept.sort((a, b) => a.floorZ - b.floorZ);

  // Slab-gap sanity between consecutive MAIN storeys that are genuinely
  // STACKED (most of the smaller footprint lies inside the larger one) —
  // a mere AABB touch is not enough, since the wall padding makes any two
  // side-by-side split-level areas overlap at their shared edge.
  const mainStoreys = kept.filter((s) => !s.isSubLevel);
  for (let i = 0; i < mainStoreys.length - 1; i++) {
    const cur = mainStoreys[i];
    const next = mainStoreys[i + 1];
    if (Math.max(overlapFractionOfFirst(cur.footprint, next.footprint), overlapFractionOfFirst(next.footprint, cur.footprint)) < 0.5) continue;
    const gap = next.floorZ - cur.ceilingZ;
    // Deeply interleaved z-ranges = side-by-side split-level areas (an AABB
    // footprint can't tell those apart) — not a slab to sanity-check. A
    // double-detected slab overlaps by centimeters at most.
    if (-gap > tuning.CEILING_CLEARANCE) continue;
    if (gap < tuning.MIN_SLAB_GAP || gap > tuning.MAX_SLAB_GAP) {
      cur.slabGapWarning = true;
      next.slabGapWarning = true;
    }
  }

  // Remap patch→storey indices to the sorted order; patches of a dropped
  // slab-crossing aggregate resolve to -1 (discarded, greyed in debug).
  const sortedIndexOfOriginal = new Array<number>(aggregates.length).fill(-1);
  kept.forEach((s, sortedIndex) => {
    sortedIndexOfOriginal[s.originalIndex] = sortedIndex;
  });
  const patchStoreyIndex = storeyOfPatch.map((s) => sortedIndexOfOriginal[s] ?? -1);

  const levels = kept.map((storey): DetectedFloorLevel => {
    const center = new THREE.Vector3();
    storey.footprint.getCenter(center);

    // Heuristic confidence: bigger supported area and tighter floor agreement
    // score higher; a suspicious slab gap costs a fixed penalty. Expected to
    // be re-tuned against real scans.
    let confidence = 0.5 * Math.min(1, storey.area / 10) + 0.5 * (1 - Math.min(1, storey.floorZSpread / 0.2));
    if (storey.slabGapWarning) confidence = Math.max(0, confidence - 0.2);

    const makePlane = (height: number, pointCount: number): DetectedFloorPlane => ({
      height,
      pointCount,
      confidence,
      bounds: new THREE.Box3(new THREE.Vector3(storey.footprint.min.x, storey.footprint.min.y, height), new THREE.Vector3(storey.footprint.max.x, storey.footprint.max.y, height)),
      center: new THREE.Vector3(center.x, center.y, height),
      normal: new THREE.Vector3(0, 0, 1),
    });

    return {
      floor: makePlane(storey.floorZ, storey.floorPointCount),
      // True ceiling with an upward normal — the confirm flow negates it.
      ceiling: makePlane(storey.ceilingZ, storey.ceilingPointCount),
      ceilingKind: 'true-ceiling',
      rooms: storey.patches,
      footprint: storey.footprint,
      footprintCorners: storey.footprintCorners,
      isSubLevel: storey.isSubLevel,
      slabGapWarning: storey.slabGapWarning,
    };
  });

  return { levels, patchStoreyIndex };
}
